/**
 * DOCUMENTATION:
 * Component: DesignPatternsBuilder
 * Purpose: Educational page teaching the Builder and Prototype design patterns.
 * * * Note for Junior Developers:
 * - Builder: "Assemble it piece by piece." Used when an object has too many optional configuration steps.
 * - Prototype: "Copy-Paste." Used when creating a new object from scratch is expensive or you need an exact clone.
 * - In modern JS, the Builder pattern is often replaced by "Configuration Objects," but the concept is
 * still vital for complex API clients and SDKs.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Fab, Zoom, Tooltip, Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1508921234172-b68ed335b3e6";

/* ✅ Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

/* ✅ Main Component */
export default function DesignPatternsBuilder() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const patterns = useMemo(() => [
        {
            name: "Builder",
            intent: "Separate the construction of a complex object from its representation so that the same construction process can create different representations.",
            useCase: "Building complex SQL queries, generating custom HTTP requests, or multi-step form data."
        },
        {
            name: "Prototype",
            intent: "Specify the kinds of objects to create using a prototypical instance, and create new objects by copying this prototype.",
            useCase: "Game development (spawning similar enemies), state hydration, or avoiding heavy re-initialization."
        }
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. The Builder Pattern (Fluent Interface)",
            code: `// ✅ Instead of a constructor with 10 arguments, use a Builder\nclass UserBuilder {\n  constructor(name) {\n    this.name = name;\n  }\n\n  setAge(age) {\n    this.age = age;\n    return this; // Returning 'this' allows method chaining\n  }\n\n  setRole(role) {\n    this.role = role;\n    return this;\n  }\n\n  build() {\n    return new User(this);\n  }\n}\n\n// Usage:\nconst myUser = new UserBuilder('Kofi')\n  .setAge(28)\n  .setRole('Admin')\n  .build();`,
            desc: "The Builder pattern makes code readable. It allows you to specify optional parameters only when you need them, without caring about their order in the constructor."
        },
        {
            title: "2. The Prototype Pattern (Cloning)",
            code: `// ✅ Cloning an object to avoid expensive re-creation\nconst carPrototype = {\n  wheels: 4,\n  init(model) {\n    this.model = model;\n    console.log(\`Ready: \${this.model} with \${this.wheels} wheels\`);\n  },\n  clone() {\n    return Object.create(this);\n  }\n};\n\nconst tesla = carPrototype.clone();\ntesla.init('Model S');\n\nconst rivian = carPrototype.clone();\nrivian.wheels = 6; // Modify the clone without affecting the prototype\nrivian.init('R1T');`,
            desc: "In JavaScript, the Prototype pattern is built into the language itself. We use it to share methods and properties between objects without duplicating them in memory."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    BUILDER & PROTOTYPE
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Simplifying Complexity
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            As applications grow, object creation becomes more complex. The <strong>Builder</strong> pattern solves the "Telescoping Constructor" anti-pattern, while the <strong>Prototype</strong> pattern leverages JavaScript's native inheritance to efficiently duplicate objects.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>Creational Power Tools</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            {patterns.map((item, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", height: "100%", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                                        <Typography variant="subtitle1" sx={{ color: "goldenrod", fontWeight: 800 }}>{item.name}</Typography>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>{item.intent}</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>USE CASE: {item.useCase}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>



                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/design-patterns-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ backgroundColor: "goldenrod", color: "#100720", fontWeight: 800, px: 4, borderRadius: "999px", "&:hover": { backgroundColor: "#e0b200" } }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
